import clsx from "clsx";
import type {
  AceStabilityCheckVariance,
  AceStabilityPhaseVariance,
  AceStabilityTestResult,
} from "@/services/api/agentReview";
import { AceStabilityCalibrationFixesPanel } from "./AceStabilityCalibrationFixesPanel";
import {
  isPartialStabilityRun,
  partialStabilityRunMessage,
} from "./aceStabilityResultHelpers";

function phaseLabel(phase: string): string {
  if (phase === "orchestration") return "Orchestration";
  if (phase === "artefact") return "Artefact";
  return "Output";
}

function phaseRowLabel(phase: AceStabilityPhaseVariance): string {
  if (phase.phase !== "artefact" || !phase.artifact_title) {
    return phaseLabel(phase.phase);
  }
  return `${phaseLabel(phase.phase)} — ${phase.artifact_title}`;
}

function checkPhaseLabel(check: AceStabilityCheckVariance): string {
  if (check.phase !== "artefact" || !check.artifact_title) {
    return phaseLabel(check.phase);
  }
  return `${phaseLabel(check.phase)} — ${check.artifact_title}`;
}

function ragSummary(counts: Record<string, number>): string {
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([rag, count]) => `${rag}: ${count}`)
    .join(", ");
}

export function AceStabilityResultView({
  result,
  catalogueKey,
}: {
  result: AceStabilityTestResult;
  catalogueKey?: string | null;
}) {
  const unstableChecks = result.checks.filter((check) => !check.stable);
  const stableChecks = result.checks.filter((check) => check.stable);
  const checkRows = [...unstableChecks, ...stableChecks];
  const partialRun = isPartialStabilityRun(result);

  return (
    <div className="space-y-4">
      {partialRun ? (
        <div className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200 ring-1 ring-amber-500/25">
          {partialStabilityRunMessage(result)}
        </div>
      ) : null}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-xs">
        <div className="rounded-md bg-white/[0.03] px-3 py-2 ring-1 ring-white/10">
          <p className="text-slate-500">Runs completed</p>
          <p className="mt-1 text-sm font-medium text-slate-100 tabular-nums">
            {result.iterations_completed} / {result.iterations_requested}
          </p>
        </div>
        <div className="rounded-md bg-white/[0.03] px-3 py-2 ring-1 ring-white/10">
          <p className="text-slate-500">Overall</p>
          <p
            className={clsx(
              "mt-1 text-sm font-medium",
              partialRun
                ? "text-amber-300"
                : result.stable
                  ? "text-emerald-300"
                  : "text-amber-300",
            )}
          >
            {partialRun ? "Incomplete" : result.stable ? "Stable" : "Variance detected"}
          </p>
        </div>
        <div className="rounded-md bg-white/[0.03] px-3 py-2 ring-1 ring-white/10">
          <p className="text-slate-500">Unstable checks</p>
          <p className="mt-1 text-sm font-medium text-slate-100 tabular-nums">
            {result.unstable_check_count}
          </p>
        </div>
        <div className="rounded-md bg-white/[0.03] px-3 py-2 ring-1 ring-white/10">
          <p className="text-slate-500">Unstable phases</p>
          <p className="mt-1 text-sm font-medium text-slate-100 tabular-nums">
            {result.unstable_phase_count}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">Phase RAGs</p>
        <div className="overflow-x-auto rounded-lg ring-1 ring-white/10">
          <table className="min-w-full text-left text-xs">
            <thead className="bg-white/[0.03] text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Phase</th>
                <th className="px-3 py-2 font-medium">Distribution</th>
                <th className="px-3 py-2 font-medium">Mode</th>
                <th className="px-3 py-2 font-medium">Stable</th>
              </tr>
            </thead>
            <tbody>
              {result.phases.map((phase) => (
                <tr key={`${phase.phase}:${phase.artifact_id ?? ""}`} className="border-t border-white/5">
                  <td className="px-3 py-2 text-slate-200">{phaseRowLabel(phase)}</td>
                  <td className="px-3 py-2 text-slate-400">{ragSummary(phase.rag_counts) || "—"}</td>
                  <td className="px-3 py-2 text-slate-300">{phase.mode_rag ?? "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      className={clsx(
                        "rounded px-1.5 py-0.5 text-[11px] font-medium",
                        phase.stable
                          ? "bg-emerald-500/15 text-emerald-300"
                          : "bg-amber-500/15 text-amber-300",
                      )}
                    >
                      {phase.stable ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
          Check answers
        </p>
        <div className="max-h-96 overflow-auto rounded-lg ring-1 ring-white/10">
          <table className="min-w-full text-left text-xs">
            <thead className="sticky top-0 bg-slate-900 text-slate-500">
              <tr>
                <th className="px-3 py-2 font-medium">Phase</th>
                <th className="px-3 py-2 font-medium">Check</th>
                <th className="px-3 py-2 font-medium">Pass</th>
                <th className="px-3 py-2 font-medium">Fail</th>
                <th className="px-3 py-2 font-medium">N/A</th>
                <th className="px-3 py-2 font-medium">Mode</th>
                <th className="px-3 py-2 font-medium">Stable</th>
              </tr>
            </thead>
            <tbody>
              {checkRows.map((check) => (
                <tr
                  key={`${check.phase}:${check.artifact_id ?? ""}:${check.check_id}`}
                  className={clsx(
                    "border-t border-white/5",
                    !check.stable && "bg-amber-500/[0.04]",
                  )}
                >
                  <td className="px-3 py-2 text-slate-400">{checkPhaseLabel(check)}</td>
                  <td className="px-3 py-2 text-slate-200">{check.check_label}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-400">{check.pass_count}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-400">{check.fail_count}</td>
                  <td className="px-3 py-2 tabular-nums text-slate-400">{check.na_count}</td>
                  <td className="px-3 py-2 text-slate-300">{check.mode_answer || "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      className={clsx(
                        "rounded px-1.5 py-0.5 text-[11px] font-medium",
                        check.stable
                          ? "bg-emerald-500/15 text-emerald-300"
                          : "bg-amber-500/15 text-amber-300",
                      )}
                    >
                      {check.stable ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AceStabilityCalibrationFixesPanel result={result} catalogueKey={catalogueKey} />
    </div>
  );
}
